/**
 * СПП варианта рядом с таблицей — «что в итоге получается» (макет v2-15).
 *
 * Карточка стоит рядом с таблицей, а не на отдельном экране, потому что она
 * отвечает на вопрос, который возникает ПРЯМО во время раскладки: «я поменял
 * поле — что стало со структурой». Ради этого ответа не должно быть перехода.
 *
 * Цифры считаются из тех же плановых посевов, что видны слева (`sowingPlanSpp`):
 * карточка и таблица на одном экране обязаны говорить одно и то же.
 */
import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { VariantField } from '@/lib/planning/planCrops';
import { sowingPlanSpp } from './sowingPlan';

const NUM = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const YIELD = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });

export function SppCard({
  fields,
  variantName,
  cropAction,
}: {
  fields: VariantField[];
  variantName: string;
  /**
   * Действие в строке культуры — сюда приезжает «Подтянуть норматив в пустые»
   * (вариант Б макета 08, AK-778). Карточка о нём ничего не знает: она про
   * структуру, а не про правку плана, и тащить сюда мутацию значило бы
   * завести второй экран правки в месте, где его не ждут.
   */
  cropAction?: (cropId: string) => ReactNode;
}) {
  const spp = sowingPlanSpp(fields);

  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-base">Структура посевных площадей</CardTitle>
        <p className="text-xs text-muted-foreground">{variantName}</p>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Культура</TableHead>
              <TableHead className="text-right">Га</TableHead>
              <TableHead className="text-right">Доля</TableHead>
              <TableHead className="text-right">Ср. урожайность, ц/га</TableHead>
              <TableHead className="text-right">Валовка, т</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {spp.rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell
                  className={cn(
                    row.kind === 'crop' && 'font-medium text-foreground',
                    row.kind === 'fallow' && 'text-muted-foreground',
                    row.kind === 'unassigned' && 'text-accent-brand',
                  )}
                >
                  {row.name}
                  {/* Ключ строки культуры — это crop_id (см. `sowingPlanSpp`);
                      служебные строки (пар, «не назначена») и запасной ключ по
                      имени действия не получают: подтягивать там нечего. */}
                  {row.kind === 'crop' && !row.key.startsWith('name:') && cropAction && (
                    <span className="mt-1 block font-normal">{cropAction(row.key)}</span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {NUM.format(Math.round(row.areaHa))}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {Math.round(row.sharePct)} %
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.yieldCHa === null ? '—' : YIELD.format(row.yieldCHa)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.grossTons === null ? '—' : NUM.format(Math.round(row.grossTons))}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-muted/50 font-semibold">
              <TableCell>Итого пашня</TableCell>
              <TableCell className="text-right tabular-nums">
                {NUM.format(Math.round(spp.totalHa))}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell className="text-right tabular-nums">
                {NUM.format(Math.round(spp.grossTons))}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Подпись основы расчёта (требование head-backend на #817). Соседний
            экран сводки варианта считает деньги по НАЗНАЧЕННЫМ ТЕХКАРТАМ, а
            здесь урожайность введена в строке поля — числа законно разные.
            Без подписи человек, сверивший их между экранами, решит, что один
            из них врёт, и перестанет верить обоим. */}
        <p className="mt-3 text-xs text-muted-foreground">
          Урожайность и валовка — по плановым посевам полей: берётся то, что вписано
          в строках этой таблицы. В сводке варианта экономика считается по назначенным
          техкартам, поэтому цифры там могут отличаться.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Средняя урожайность — взвешенная по гектарам. Валовка = Σ (га × урожайность).
        </p>
        {spp.hasYieldGaps && (
          <p className="mt-1 text-xs text-accent-brand">
            У части полей урожайность не заполнена — по ним валовка не посчитана, поэтому
            итог занижен. Это честнее, чем домножить их на среднюю.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
